import { createStore } from 'zustand/vanilla';

import { ModuleSocketClient } from '@MP/shared/socket/scanModuleSocket';
import { OrgSocketClient } from '@MP/shared/socket/scanOrgSocket';
import { ScanSocket } from '@MP/shared/socket/scanSocket';

import { DeviceSerial, ScanModule } from '../types';
import ModulesStatusManager from '../utils/ModulesStatusManager';

/**
 * 모듈 상태 변화는 [module_state.png] 이미지 참고
 */
class ScanModules {
  private _moduleMap: Record<DeviceSerial, ScanModule> = {};
  private modules: Readonly<ScanModule>[];
  constructor(modules: Readonly<ScanModule>[]) {
    this.modules = modules;
    modules.forEach((module) => {
      this._moduleMap[module.deviceSerial] = module;
    });
  }

  get list() {
    return this.modules;
  }

  getModule(moduleSerial: DeviceSerial): ScanModule | undefined {
    return this._moduleMap[moduleSerial];
  }
}

export interface ScanModulesState {
  orgClient: OrgSocketClient | null;
  moduleClients: Record<string, ModuleSocketClient>;
  statusManager: ModulesStatusManager | null;
  scanModules: ScanModules;
  init: (deviceSerials: DeviceSerial[]) => Promise<void>;
  clearModule: () => void;
  getModuleClient: (deviceSerial: DeviceSerial) => ModuleSocketClient | null;
  getModuleByUser: (userId: string) => ScanModule | undefined;
  getModule: (moduleSerial: DeviceSerial) => ScanModule | undefined;
  clear: () => void;
}

export const createScanModulesStore = () =>
  createStore<ScanModulesState>((set, get) => {
    const initializeOrgClient = (orgClient: OrgSocketClient) => {
      orgClient.subscribeOnRegistration(addModule);
      orgClient.subscribeOnDisconnected(removeModule);
    };

    const initializeModules = (orgClient: OrgSocketClient, deviceSerials: DeviceSerial[]) => {
      try {
        const statusManager = ModulesStatusManager.create(orgClient, deviceSerials, (modules: Readonly<ScanModule>[]) =>
          refreshModule(modules)
        );
        set({ statusManager });
      } catch (error) {
        console.error('Error initializing modules:', error);
      }
    };

    const refreshModule = (modules: Readonly<ScanModule>[]) => {
      set({ scanModules: new ScanModules(modules) });
    };

    const addModule = (deviceSerial: DeviceSerial) => {
      get().statusManager?.addModules(deviceSerial);
    };

    const removeModule = (deviceSerial: DeviceSerial) => {
      get().moduleClients[deviceSerial]?.disconnect();
      const moduleClients = { ...get().moduleClients };
      delete moduleClients[deviceSerial];
      set({ moduleClients });
    };

    return {
      orgClient: null,
      moduleClients: {},
      statusManager: null,
      scanModules: new ScanModules([]),

      init: async (deviceSerials: DeviceSerial[]) => {
        const orgClient = await ScanSocket.connectOrg();
        set({ orgClient });
        initializeOrgClient(orgClient);
        initializeModules(orgClient, deviceSerials);
      },

      clearModule: () => {
        Object.values(get().moduleClients).forEach((client) => {
          client.disconnect();
        });
        get().statusManager?.destroy();
        set({ statusManager: null, moduleClients: {}, scanModules: new ScanModules([]) });
      },

      getModuleClient: (deviceSerial: DeviceSerial) => {
        if (deviceSerial) {
          if (!get().moduleClients[deviceSerial]) {
            const moduleClients = { ...get().moduleClients, [deviceSerial]: ScanSocket.connectModule(deviceSerial) };
            set({ moduleClients });
          }

          return get().moduleClients[deviceSerial];
        }
        return null;
      },

      getModuleByUser: (userId: string) => {
        return get().scanModules.list.find((m) => m.userId === userId);
      },

      getModule: (moduleSerial: DeviceSerial) => {
        return get().scanModules.getModule(moduleSerial);
      },

      clear: () => {
        get().clearModule();
        ScanSocket.disconnectOrg();
      },
    };
  });

export type ScanModulesStoreApi = ReturnType<typeof createScanModulesStore>;

export const selectModules = (s: ScanModulesState): Readonly<ScanModule>[] => s.scanModules.list;
